import React from "react";
import { Card, Flex, Link, Text, toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { useGetCountryCodesQuery } from "#graphql/supergraphHooks";
import { getCountryNameFromId } from "#utils/country_utils";
import { clientContext } from "#utils/graphql_utils";
import { capitalizeFirstLetter } from "../../../../../common/utils";
import { DocumentTracker } from "../../../components/DocumentTracker";
import { FormStepFooter } from "../../../components/FormStepFooter";
import { PayeePaymentPrefDisplayCard } from "../../../components/PayeePaymentPrefDisplayCard";
import { PaymentDetailField } from "../../../components/PaymentDetailField";
import { PayeeStepActionTypes, PayeeWorkflowModes } from "../../../Context/constants";
import {
  useBasePayeeReducerContext,
  usePayeeWorkflowDispatchContext,
  usePayeeWorkflowContext,
} from "../../../Context/PayeeContext";
import {
  payeeDetailsColumnStyles,
  stepColumnContainerStyles,
  stepContainerStyles,
} from "../../../styles_vanilla.css";
import { SubmitForReviewText } from "../../constants";
import { TaxDocumentType } from "../../types";
import { TAX_FORM_DISPLAY_NAMES, TaxFormType } from "../VendorPayeeDetailsStep/constants";
import { useSaveVendorPayeeHandler } from "./hooks/useSaveVendorPayeeHandler";
import { payeeDetailHeadingStyles } from "./styles_vanilla.css";

type VendorReviewStepProps = { onPayeeCreateSuccess?: (createdPayee?: unknown) => void };

export const VendorReviewStep: React.FC<VendorReviewStepProps> = ({
  onPayeeCreateSuccess,
}: VendorReviewStepProps) => {
  const stepDispatch = usePayeeWorkflowDispatchContext();
  const { mode } = usePayeeWorkflowContext();
  const {
    invoices,
    payeeName,
    arenaId,
    arenaName,
    payeeAddress,
    form1099Required,
    taxDocuments,
    taxCountryId,
    taxId,
    paymentPref,
  } = useBasePayeeReducerContext();

  // Query country data to get country name for display
  const { data: countryData } = useGetCountryCodesQuery({
    context: clientContext(),
  });

  const { savePayee, loading } = useSaveVendorPayeeHandler(
    onPayeeCreateSuccess ? { onSuccess: onPayeeCreateSuccess } : undefined,
  );

  const paymentPrefId = paymentPref?.id;

  const title_card =
    mode === PayeeWorkflowModes.EDIT ? "Review payee changes" : "Review new payee";

  if (!payeeName || !arenaId || !invoices || !paymentPrefId) {
    toast.error("Missing required information");
    return null;
  }

  const handleSubmit = async () => {
    await savePayee();
  };

  // Get address display value or empty string if undefined
  const getAddressValue = (value?: string) => {
    return value || "";
  };

  // Get state or region display
  const getStateOrRegion = () => {
    if (payeeAddress?.stateId) {
      return payeeAddress.stateId;
    } else if (payeeAddress?.region) {
      return payeeAddress.region;
    }
    return "";
  };

  return (
    <div className={stepContainerStyles}>
      <Form initialValues={{}} onSubmit={handleSubmit}>
        <ColumnContainer className={stepColumnContainerStyles}>
          <Column size={"fluid"}>
            <DocumentTracker
              documentName={invoices?.[0]?.name ?? ""}
              documentUrl={invoices?.[0]?.localUrl ?? ""}
            />
          </Column>
          <Column size={"fluid"} className={payeeDetailsColumnStyles}>
            <Card padding="lg" title={title_card}>
              <Flex flexDirection="column" gap={16}>
                <Text variant="strong-md">Client details</Text>
                <PaymentDetailField label="Client" value={arenaName} />

                <Text variant="strong-md" className={payeeDetailHeadingStyles}>
                  Payee details
                </Text>
                <PaymentDetailField label="Vendor" value={payeeName} />

                <Text variant="strong-md" className={payeeDetailHeadingStyles}>
                  Address
                </Text>
                <PaymentDetailField
                  label="Street"
                  value={getAddressValue(payeeAddress?.street)}
                />
                <PaymentDetailField
                  label="Street 2"
                  value={getAddressValue(payeeAddress?.street2)}
                />
                <PaymentDetailField label="City" value={getAddressValue(payeeAddress?.city)} />
                <PaymentDetailField label="State/Region" value={getStateOrRegion()} />
                <PaymentDetailField
                  label="Postal Code"
                  value={getAddressValue(payeeAddress?.postalCode)}
                />
                {payeeAddress?.countryId && (
                  <PaymentDetailField
                    label="Country"
                    value={(() => {
                      // Get country name from alpha2code since payeeAddress.countryId stores alpha2code format
                      if (!payeeAddress.countryId || !countryData?.countries?.edges) return "";
                      const country = countryData.countries.edges.find(
                        (edge) => edge?.node?.alpha2code === payeeAddress.countryId,
                      );
                      return country?.node?.name || "";
                    })()}
                  />
                )}

                <Text variant="strong-md" className={payeeDetailHeadingStyles}>
                  Tax Information
                </Text>
                {taxId && (
                  <PaymentDetailField
                    label="Tax ID"
                    value={`${taxId} (${getCountryNameFromId(taxCountryId, countryData?.countries)})`}
                  />
                )}
                <PaymentDetailField
                  label="1099 Required"
                  value={
                    form1099Required === undefined
                      ? ""
                      : capitalizeFirstLetter(form1099Required)
                  }
                />
                {taxDocuments && taxDocuments.length > 0 && (
                  <PaymentDetailField
                    label="Tax Form"
                    value={`${taxDocuments[0].documentType === TaxDocumentType.US_TAX ? TAX_FORM_DISPLAY_NAMES[TaxFormType.W9] : TAX_FORM_DISPLAY_NAMES[TaxFormType.W8]}`}
                    valueChildren={
                      <Link
                        href={taxDocuments[0].localUrl}
                        shouldOpenInNewTab
                        align="left"
                        icon="external-link"
                        iconPosition="right"
                      >
                        {taxDocuments[0].name}
                      </Link>
                    }
                  />
                )}

                <Text variant="strong-md" className={payeeDetailHeadingStyles}>
                  Invoice details
                </Text>
                <PaymentDetailField label="Invoice" value={invoices?.[0]?.name ?? ""} />
                <Text variant="strong-md" className={payeeDetailHeadingStyles}>
                  Payment instructions
                </Text>
                <PayeePaymentPrefDisplayCard paymentPref={paymentPref} />
              </Flex>
            </Card>
          </Column>
        </ColumnContainer>
        <FormStepFooter
          onBack={() => {
            stepDispatch({ type: PayeeStepActionTypes.DECREMENT_STEP });
          }}
          submitText={SubmitForReviewText}
          submitDisabled={loading}
        />
      </Form>
    </div>
  );
};

export default VendorReviewStep;
